/*
 * SystemDeploymentsSummary - 部署状态聚合摘要
 * 展示 System 下所有组件在各环境的部署状态统计
 * 使用真实的 ArgoCD 数据
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
  LinearProgress,
  Tooltip,
  makeStyles,
  createStyles,
  Theme,
  Chip,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import HelpIcon from '@material-ui/icons/Help';
import SyncIcon from '@material-ui/icons/Sync';
import PauseCircleFilledIcon from '@material-ui/icons/PauseCircleFilled';
import WarningIcon from '@material-ui/icons/Warning';
import { useEntity, useRelatedEntities } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import { RELATION_HAS_PART } from '@backstage/catalog-model';
import { Progress, EmptyState } from '@backstage/core-components';
import { argoCDApiRef } from '@roadiehq/backstage-plugin-argo-cd';
import { kubernetesApiRef } from '@backstage/plugin-kubernetes';
import { parseEnvironmentConfigs, EnvironmentConfig, getArgoCDProjectInfo } from './types';
import { ComponentDetailCard, ComponentDetailData } from './ComponentDetailCard';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      height: '100%',
    },
    summaryBox: {
      padding: theme.spacing(2),
      borderRadius: theme.spacing(1),
      textAlign: 'center',
      height: '100%',
    },
    statNumber: {
      fontSize: '2.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
    },
    statLabel: {
      fontSize: '0.875rem',
      color: theme.palette.text.secondary,
      marginTop: theme.spacing(0.5),
    },
    componentSection: {
      marginTop: theme.spacing(3),
    },
    envSection: {
      marginTop: theme.spacing(3),
    },
    envHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: theme.spacing(1),
    },
    envTitle: {
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    progressBar: {
      height: 8,
      borderRadius: 4,
      '& .MuiLinearProgress-bar': {
        borderRadius: 4,
      },
    },
    progressBarError: {
      '& .MuiLinearProgress-bar': {
        backgroundColor: '#f44336',
      },
    },
    progressBarWarning: {
      '& .MuiLinearProgress-bar': {
        backgroundColor: '#ff9800',
      },
    },
    progressBarSuccess: {
      '& .MuiLinearProgress-bar': {
        backgroundColor: '#4caf50',
      },
    },
    statusIcon: {
      fontSize: '1rem',
    },
    statusRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(2),
      marginTop: theme.spacing(1),
    },
    statusItem: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      fontSize: '0.875rem',
    },
    healthyIcon: {
      color: '#4caf50',
    },
    degradedIcon: {
      color: '#f44336',
    },
    progressingIcon: {
      color: '#2196f3',
    },
    suspendedIcon: {
      color: '#ff9800',
    },
    unknownIcon: {
      color: '#9e9e9e',
    },
    noDataText: {
      color: theme.palette.text.secondary,
      fontStyle: 'italic',
    },
  }),
);

// ArgoCD 健康状态类型
type HealthStatus = 'Healthy' | 'Progressing' | 'Degraded' | 'Suspended' | 'Missing' | 'Unknown';

// 组件部署状态（使用共享的 ComponentDetailData 类型）
type ComponentAppStatus = ComponentDetailData;

const StatusIcon: React.FC<{ status: HealthStatus }> = ({ status }) => {
  const classes = useStyles();
  
  switch (status) {
    case 'Healthy':
      return <CheckCircleIcon className={`${classes.statusIcon} ${classes.healthyIcon}`} />;
    case 'Degraded':
    case 'Missing':
      return <ErrorIcon className={`${classes.statusIcon} ${classes.degradedIcon}`} />;
    case 'Progressing':
      return <SyncIcon className={`${classes.statusIcon} ${classes.progressingIcon}`} />;
    case 'Suspended':
      return <PauseCircleFilledIcon className={`${classes.statusIcon} ${classes.suspendedIcon}`} />;
    default:
      return <HelpIcon className={`${classes.statusIcon} ${classes.unknownIcon}`} />;
  }
};


/**
 * 根据 destination 信息匹配到 System 的环境配置
 */
const matchEnvironment = (
  clusterName: string | undefined,
  server: string | undefined,
  namespace: string | undefined,
  environments: EnvironmentConfig[],
): { name: string; label: string } | undefined => {
  if (!clusterName && !server && !namespace) {
    return undefined;
  }

  for (const env of environments) {
    // 匹配逻辑：优先匹配 clusterName，其次匹配 server，最后匹配 namespace
    const clusterMatch = clusterName && (
      env.clusterName === clusterName ||
      env.clusterName === clusterName.replace(/^https?:\/\//, '').split(':')[0]
    );
    const serverMatch = server && (
      env.server === server ||
      env.server.replace(/^https?:\/\//, '').split(':')[0] === server.replace(/^https?:\/\//, '').split(':')[0]
    );
    const namespaceMatch = namespace && env.namespace === namespace;

    if (clusterMatch || (serverMatch && namespaceMatch)) {
      return { name: env.name, label: env.label };
    }
  }

  return undefined;
};

export const SystemDeploymentsSummary: React.FC = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const argoCDApi = useApi(argoCDApiRef);
  const kubernetesApi = useApi(kubernetesApiRef);

  // 使用 useMemo 稳定 environments，避免无限循环
  // 使用 entity 的稳定标识符（etag 或 uid）作为依赖
  const entityId = entity.metadata.uid || entity.metadata.etag || `${entity.metadata.namespace || 'default'}/${entity.metadata.name}`;
  // entityId 已经包含了 entity 的关键信息，使用它作为依赖可以避免无限循环
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const environments = useMemo(() => parseEnvironmentConfigs(entity), [entityId]);

  // 获取 System 的 ArgoCD Project 信息（用于获取默认 instance-name）
  // entityId 已经包含了 entity 的关键信息，使用它作为依赖可以避免无限循环
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const argocdProject = useMemo(() => getArgoCDProjectInfo(entity), [entityId]);

  // 获取 System 下的所有 Component
  const { entities: components, loading: componentsLoading, error: componentsError } = useRelatedEntities(entity, {
    type: RELATION_HAS_PART,
    kind: 'Component',
  });

  // ArgoCD 应用状态
  const [appStatuses, setAppStatuses] = useState<ComponentAppStatus[]>([]);
  const [argoLoading, setArgoLoading] = useState(false);
  const [argoError, setArgoError] = useState<Error | null>(null);

  // 获取每个组件的 ArgoCD 应用状态
  // 使用 components 的稳定标识符作为依赖，避免无限循环
  const componentsKey = useMemo(
    () => components?.map(c => `${c.metadata.namespace || 'default'}/${c.metadata.name}`).sort().join(',') || '',
    [components],
  );

  useEffect(() => {
    if (!components || components.length === 0) {
      setAppStatuses([]);
      setArgoLoading(false);
      return undefined;
    }

    let cancelled = false;

    const fetchArgoStatus = async () => {
      setArgoLoading(true);
      setArgoError(null);

      const statuses: ComponentAppStatus[] = [];

      for (const component of components) {
        const annotations = component.metadata?.annotations || {};
        const poweredBy = annotations['hose.io/powered-by'];
        const appName = annotations['argocd/app-name'];
        const appSelector = annotations['argocd/app-selector'];
        
        // 检查是否是 Knative 管理的组件
        if (poweredBy === 'knative') {
          const knativeServiceName = annotations['knative.dev/service-name'];
          const knativeNamespace = annotations['knative.dev/service-namespace'] || annotations['backstage.io/kubernetes-namespace'];
          const clusterName = annotations['backstage.io/kubernetes-cluster-name'];
          const environment = annotations['ekuaibao.com/environment'];
          
          // 尝试获取 Knative Service 状态
          let knativeStatus: any = null;
          let latestRevision = '';
          let trafficInfo = '';
          let knativeHealthStatus: HealthStatus = 'Unknown';
          
          if (knativeServiceName && knativeNamespace && clusterName) {
            try {
              const response = await kubernetesApi.proxy({
                clusterName,
                path: `/apis/serving.knative.dev/v1/namespaces/${knativeNamespace}/services/${knativeServiceName}`,
              });
              if (response.ok) {
                knativeStatus = await response.json();
                
                // 获取最新 Revision
                latestRevision = knativeStatus.status?.latestReadyRevisionName || '';
                
                // 获取流量分配信息
                const traffic = knativeStatus.status?.traffic || [];
                if (traffic.length > 0) {
                  // 简短显示：只显示 Revision 后缀和百分比
                  trafficInfo = traffic
                    .filter((t: any) => t.percent > 0)
                    .map((t: any) => {
                      const revisionName = t.revisionName || 'latest';
                      const shortName = revisionName.split('-').pop() || 'latest';
                      return `${shortName}:${t.percent}%`;
                    })
                    .join(', ');
                }
                
                // 判断健康状态 - 参考 getKnativeServiceStatus 逻辑
                const conditions = knativeStatus.status?.conditions || [];
                const readyCondition = conditions.find((c: any) => c.type === 'Ready');
                
                if (!readyCondition) {
                  knativeHealthStatus = 'Unknown';
                } else if (readyCondition.status === 'True') {
                  knativeHealthStatus = 'Healthy';
                } else if (readyCondition.status === 'False') {
                  knativeHealthStatus = 'Degraded';
                } else {
                  // status === 'Unknown'，检查是否正在部署
                  const configurationsReadyCondition = conditions.find(
                    (c: any) => c.type === 'ConfigurationsReady'
                  );
                  if (configurationsReadyCondition?.status === 'Unknown') {
                    knativeHealthStatus = 'Progressing';
                  } else {
                    knativeHealthStatus = 'Unknown';
                  }
                }
              }
            } catch (err) {
              // 获取 Knative 状态失败，使用默认值
            }
          }
          
          statuses.push({
            componentName: component.metadata.name,
            componentRef: `component:${component.metadata.namespace || 'default'}/${component.metadata.name}`,
            componentType: component.spec?.type as string | undefined,
            componentDescription: component.metadata.description,
            hasArgoCD: false,
            healthStatus: knativeHealthStatus,
            syncStatus: latestRevision ? 'Active' : 'Unknown',
            poweredBy: 'knative',
            knativeServiceName,
            knativeNamespace,
            clusterName,
            environment,
            environmentLabel: environment?.toUpperCase(),
            // 复用 syncRevision 和 lastSyncTime 字段存储 Knative 信息
            syncRevision: latestRevision,
            lastSyncTime: trafficInfo,
          });
          continue;
        }
        
        // 获取 ArgoCD 实例名称：优先使用 component 注解，其次使用 System 的 ArgoCD Project，最后使用默认值
        const instanceName = annotations['argocd/instance-name'] || argocdProject?.instanceName || 'development';

        if (!appName && !appSelector) {
          // 没有 ArgoCD 配置
          statuses.push({
            componentName: component.metadata.name,
            componentRef: `component:${component.metadata.namespace || 'default'}/${component.metadata.name}`,
            componentType: component.spec?.type as string | undefined,
            componentDescription: component.metadata.description,
            hasArgoCD: false,
            healthStatus: 'Unknown',
            syncStatus: 'Unknown',
          });
          continue;
        }

        try {
          let apps: any[] = [];

          if (appName) {
            // 获取特定应用
            const appDetails = await argoCDApi.getAppDetails({
              url: `/argoInstance/${instanceName}`,
              appName,
              instance: instanceName,
            });
            if (appDetails) {
              apps = [appDetails];
            }
          } else if (appSelector) {
            // 使用选择器获取应用
            const response = await argoCDApi.getAppListDetails({
              url: `/argoInstance/${instanceName}`,
              appSelector,
              instance: instanceName,
            });
            apps = response?.items || [];
          }

          if (apps.length > 0) {
            // 取第一个应用的状态（或聚合多个应用的状态）
            const app = apps[0];
            const appAny = app as any;
            
            // 获取 destination 信息
            const dest = appAny.spec?.destination || {};
            // 优先使用 component 注解中的 argocd/dest-server 作为 clusterName
            const clusterNameFromAnnotation = annotations['argocd/dest-server'];
            const clusterName = clusterNameFromAnnotation || dest.name;
            const server = dest.server;
            const namespace = dest.namespace || 'default';
            
            // 匹配环境
            const matchedEnv = matchEnvironment(clusterName, server, namespace, environments);
            
            statuses.push({
              componentName: component.metadata.name,
              componentRef: `component:${component.metadata.namespace || 'default'}/${component.metadata.name}`,
              componentType: component.spec?.type as string | undefined,
              componentDescription: component.metadata.description,
              appName: app.metadata?.name,
              instanceName,
              hasArgoCD: true,
              healthStatus: (app.status?.health?.status as HealthStatus) || 'Unknown',
              syncStatus: app.status?.sync?.status || 'Unknown',
              clusterName,
              server,
              namespace,
              environment: matchedEnv?.name,
              environmentLabel: matchedEnv?.label,
              lastSyncTime: app.status?.operationState?.finishedAt || app.status?.sync?.revision,
              syncRevision: app.status?.sync?.revision,
            });
          } else {
            statuses.push({
              componentName: component.metadata.name,
              componentRef: `component:${component.metadata.namespace || 'default'}/${component.metadata.name}`,
              componentType: component.spec?.type as string | undefined,
              componentDescription: component.metadata.description,
              appName,
              instanceName,
              hasArgoCD: true,
              healthStatus: 'Missing',
              syncStatus: 'Unknown',
            });
          }
        } catch (err) {
          statuses.push({
            componentName: component.metadata.name,
            componentRef: `component:${component.metadata.namespace || 'default'}/${component.metadata.name}`,
            componentType: component.spec?.type as string | undefined,
            componentDescription: component.metadata.description,
            appName,
            instanceName,
            hasArgoCD: true,
            healthStatus: 'Unknown',
            syncStatus: 'Unknown',
          });
        }
      }

      if (!cancelled) {
        setAppStatuses(statuses);
        setArgoLoading(false);
      }
    };

    fetchArgoStatus();

    return () => {
      cancelled = true;
    };
    // 使用稳定的 componentsKey 和 environments 的标识符作为依赖，避免无限循环
    // components 在 componentsKey 中已经体现，environments 在 entityId 中已经体现
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentsKey, entityId, argoCDApi]);

  // 计算统计数据
  const stats = useMemo(() => {
    const result = {
      total: appStatuses.length,
      healthy: 0,
      degraded: 0,
      progressing: 0,
      suspended: 0,
      unknown: 0,
      noArgoCD: 0,
      knative: 0,  // Knative 管理的组件数量
      knativeHealthy: 0,  // Knative 健康组件数量
      knativeDegraded: 0,  // Knative 异常组件数量
      knativeProgressing: 0,  // Knative 部署中组件数量
      knativeUnknown: 0,  // Knative 未知状态组件数量
    };

    appStatuses.forEach((status) => {
      // Knative 组件统计
      if (status.poweredBy === 'knative') {
        result.knative++;
        // 同时统计 Knative 组件的健康状态
        switch (status.healthStatus) {
          case 'Healthy':
            result.healthy++;
            result.knativeHealthy++;
            break;
          case 'Degraded':
          case 'Missing':
            result.degraded++;
            result.knativeDegraded++;
            break;
          case 'Progressing':
            result.progressing++;
            result.knativeProgressing++;
            break;
          default:
            result.unknown++;
            result.knativeUnknown++;
        }
        return;
      }
      
      if (!status.hasArgoCD) {
        result.noArgoCD++;
        return;
      }

      switch (status.healthStatus) {
        case 'Healthy':
          result.healthy++;
          break;
        case 'Degraded':
        case 'Missing':
          result.degraded++;
          break;
        case 'Progressing':
          result.progressing++;
          break;
        case 'Suspended':
          result.suspended++;
          break;
        default:
          result.unknown++;
      }
    });

    return result;
  }, [appStatuses]);

  const loading = componentsLoading || argoLoading;
  const error = componentsError || argoError;

  if (loading) {
    return <Progress />;
  }

  if (error) {
    return (
      <Card className={classes.card}>
        <CardHeader title="部署状态概览" />
        <CardContent>
          <EmptyState
            missing="info"
            title="Failed to load"
            description={error.message}
          />
        </CardContent>
      </Card>
    );
  }

  if (!components || components.length === 0) {
    return (
      <Card className={classes.card}>
        <CardHeader title="部署状态概览" />
        <CardContent>
          <EmptyState
            missing="data"
            title="No Components"
            description="No components registered under this System yet."
          />
        </CardContent>
      </Card>
    );
  }

  const totalManaged = stats.total - stats.noArgoCD;
  const healthyPercent = totalManaged > 0 ? (stats.healthy / totalManaged) * 100 : 0;
  
  // 确定进度条颜色类
  let progressBarColorClass = classes.progressBarSuccess;
  if (healthyPercent < 50) {
    progressBarColorClass = classes.progressBarError;
  } else if (healthyPercent < 100) {
    progressBarColorClass = classes.progressBarWarning;
  }

  return (
    <Card className={classes.card}>
      <CardHeader title="部署状态概览" />
      <CardContent>
        {/* 总体统计 */}
        <Grid container spacing={2}>
          <Grid item xs={6} sm>
            <Box className={classes.summaryBox} style={{ backgroundColor: '#e0f7fa' }}>
              <Typography className={classes.statNumber} style={{ color: '#00838f' }}>
                {environments.length}
              </Typography>
              <Typography className={classes.statLabel}>环境</Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm>
            <Box className={classes.summaryBox} style={{ backgroundColor: '#e3f2fd' }}>
              <Typography className={classes.statNumber} style={{ color: '#1565c0' }}>
                {stats.total}
              </Typography>
              <Typography className={classes.statLabel}>Components</Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm>
            <Box className={classes.summaryBox} style={{ backgroundColor: '#e8f5e9' }}>
              <Typography className={classes.statNumber} style={{ color: '#2e7d32' }}>
                {stats.healthy}
              </Typography>
              <Typography className={classes.statLabel}>Healthy</Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm>
            <Box className={classes.summaryBox} style={{ backgroundColor: stats.degraded > 0 ? '#ffebee' : '#f5f5f5' }}>
              <Typography
                className={classes.statNumber}
                style={{ color: stats.degraded > 0 ? '#c62828' : '#757575' }}
              >
                {stats.degraded}
              </Typography>
              <Typography className={classes.statLabel}>Degraded</Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm>
            <Box className={classes.summaryBox} style={{ backgroundColor: stats.progressing > 0 ? '#e3f2fd' : '#f5f5f5' }}>
              <Typography
                className={classes.statNumber}
                style={{ color: stats.progressing > 0 ? '#1976d2' : '#757575' }}
              >
                {stats.progressing}
              </Typography>
              <Typography className={classes.statLabel}>Progressing</Typography>
            </Box>
          </Grid>
        </Grid>

        {/* 健康度进度条 */}
        <Box className={classes.envSection}>
          <Box className={classes.envHeader}>
            <Typography className={classes.envTitle}>
              Overall Health
            </Typography>
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              {healthyPercent < 100 && (
                <Chip
                  icon={<WarningIcon />}
                  label={`仅 ${healthyPercent.toFixed(0)}% 健康 (${stats.healthy}/${stats.total - stats.noArgoCD})`}
                  size="small"
                  style={{
                    backgroundColor: healthyPercent < 50 ? '#ffebee' : '#fff3e0',
                    color: healthyPercent < 50 ? '#c62828' : '#e65100',
                    fontWeight: 600,
                  }}
                />
              )}
              {healthyPercent === 100 && (
            <Typography variant="body2" color="textSecondary">
              {stats.healthy}/{stats.total - stats.noArgoCD} healthy
            </Typography>
              )}
            </Box>
          </Box>
          <Tooltip
            title={`Healthy: ${stats.healthy}, Degraded: ${stats.degraded}, Progressing: ${stats.progressing}, Suspended: ${stats.suspended}, Unknown: ${stats.unknown}`}
            arrow
          >
            <LinearProgress
              variant="determinate"
              value={healthyPercent}
              className={`${classes.progressBar} ${progressBarColorClass}`}
              style={{ backgroundColor: '#e0e0e0' }}
            />
          </Tooltip>
          <Box className={classes.statusRow}>
            {stats.healthy > 0 && (
              <Box className={classes.statusItem}>
                <StatusIcon status="Healthy" />
                <span>{stats.healthy} Healthy</span>
              </Box>
            )}
            {stats.degraded > 0 && (
              <Box className={classes.statusItem}>
                <StatusIcon status="Degraded" />
                <span>{stats.degraded} Degraded</span>
              </Box>
            )}
            {stats.progressing > 0 && (
              <Box className={classes.statusItem}>
                <StatusIcon status="Progressing" />
                <span>{stats.progressing} Progressing</span>
              </Box>
            )}
            {stats.suspended > 0 && (
              <Box className={classes.statusItem}>
                <StatusIcon status="Suspended" />
                <span>{stats.suspended} Suspended</span>
              </Box>
            )}
            {stats.knative > 0 && (
              <Box className={classes.statusItem}>
                <span style={{ 
                  display: 'inline-flex', 
                  alignItems: 'center', 
                  gap: 4,
                  color: '#1565c0',
                  fontSize: '0.75rem',
                }}>
                  ☁️ {stats.knative} Knative
                  {(stats.knativeDegraded > 0 || stats.knativeProgressing > 0 || stats.knativeUnknown > 0) && (
                    <span style={{ marginLeft: 4, color: '#757575' }}>
                      (
                      {stats.knativeDegraded > 0 && <span style={{ color: '#f44336' }}>{stats.knativeDegraded} Degraded</span>}
                      {stats.knativeDegraded > 0 && (stats.knativeProgressing > 0 || stats.knativeUnknown > 0) && <span>, </span>}
                      {stats.knativeProgressing > 0 && <span style={{ color: '#2196f3' }}>{stats.knativeProgressing} Progressing</span>}
                      {stats.knativeProgressing > 0 && stats.knativeUnknown > 0 && <span>, </span>}
                      {stats.knativeUnknown > 0 && <span style={{ color: '#9e9e9e' }}>{stats.knativeUnknown} Unknown</span>}
                      )
                    </span>
                  )}
                </span>
              </Box>
            )}
            {stats.noArgoCD > 0 && (
              <Box className={classes.statusItem}>
                <StatusIcon status="Unknown" />
                <span>{stats.noArgoCD} No ArgoCD</span>
              </Box>
            )}
          </Box>
        </Box>

        {/* 各组件详细信息列表 */}
        <Box className={classes.componentSection}>
          <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600, fontSize: '1rem' }}>
            组件详情
          </Typography>
          {appStatuses.map((status) => (
            <ComponentDetailCard key={status.componentRef} detail={status} />
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};
